import {
  Component,
  inject,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSortModule, Sort, SortDirection } from '@angular/material/sort';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomersService } from '../../services/customers.service';
import { awaitingErpBalanceHint, InvoiceModel, InvoiceMetadataResponse, InvoiceTimelineEvent } from '../../customer.model';
import {
  TestAddInvoicesModalComponent,
  TestInvoice,
} from '../test-add-invoices-modal/test-add-invoices-modal.component';
import { UserStore } from 'app/features/user/store/user.store';
import {
  isZeroAmount,
  LEDGER_DOCUMENT_STATUS_HINTS,
  LEDGER_DOCUMENT_STATUS_LABELS,
  LedgerDocumentStatus,
} from '../../models/customer-ledger.model';

/** Document-type filter chips → one or more backend invoiceType values. */
interface TypeChip {
  key: string;
  label: string;
  types: string[];
}

/**
 * Credit-type documents (FTE `CREDIT_INVOICE_TYPES`). Their amounts are stored negative — a credit
 * lowers what the customer owes — so "paid" on one is the credit APPLIED, which is shown as a
 * positive amount: applying a $110 credit is $110 applied, never "−$110 paid".
 */
const CREDIT_DOCUMENT_TYPES = new Set(['ERPCreditMemo', 'ERPCreditOnFile', 'Coupon', 'Adjustment']);

/** Display names for backend invoiceType values; anything unlisted shows as stored. */
const INVOICE_TYPE_LABELS: Record<string, string> = {
  ERPInvoice: 'Invoice',
  ERPCreditMemo: 'Credit Memo',
};

/** Chips offered whether or not the customer holds any of that type. */
const ALWAYS_SHOWN_CHIPS = new Set(['invoice', 'creditMemo']);

const TYPE_CHIPS: TypeChip[] = [
  { key: 'invoice', label: 'Invoice', types: ['ERPInvoice'] },
  { key: 'creditMemo', label: 'Credit Memo', types: ['ERPCreditMemo'] },
  { key: 'creditOnFile', label: 'Credit on File', types: ['ERPCreditOnFile'] },
  { key: 'financeCharge', label: 'Finance Charge', types: ['ERPFinanceCharges'] },
  { key: 'ccFee', label: 'CC Fee', types: ['CCFeeFreely', 'CCFeeExternal'] },
  { key: 'adjustment', label: 'Adjustment', types: ['Adjustment'] },
  { key: 'coupon', label: 'Coupon', types: ['Coupon'] },
  { key: 'serviceFee', label: 'Service Fee', types: ['ServiceFee'] },
  { key: 'adhoc', label: 'Adhoc', types: ['AdhocInvoice'] },
];

/** One flattened `invoiceMetadata` leaf, ready to render as a label/value pair. */
export interface MetadataEntry {
  path: string;
  value: string;
}

/**
 * Metadata paths the drawer never shows.
 *
 * `originContext.importId` is the ERP run's internal uuid — it identifies a row in
 * `ERPImportHistory`, not anything about the document, and reads as noise next to
 * the fields that describe the invoice. It is still returned by the endpoint and
 * by `fte-cli invoices metadata`, where tracing a document back to its run is the
 * whole point; this list is about what the drawer renders.
 */
const HIDDEN_METADATA_PATHS: ReadonlySet<string> = new Set(['originContext.importId']);

/**
 * Flatten the metadata blob to dotted paths so a nested, per-ERP-shaped object
 * renders as a flat list rather than as a wall of raw JSON. Arrays keep their
 * index in the path.
 *
 * Empty leaves are **dropped, not rendered as placeholders**. Most feeds write
 * their whole shape whether or not they have values for it — `erp.companyNumber`,
 * `divisionNumber`, `departmentNumber` and `statementAccountNumber` all arrive as
 * `""` on an Entrée V5 document — so keeping them turns the drawer into mostly
 * blanks and buries the fields that do say something. A container whose leaves are
 * all empty contributes nothing and so disappears with them.
 *
 * `0` and `false` are values, not blanks, and are kept.
 */
function flattenMetadata(value: unknown, prefix = ''): MetadataEntry[] {
  if (value === null || value === undefined) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.flatMap((item, i) => flattenMetadata(item, `${prefix}[${i}]`));
  }
  if (typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>).flatMap((key) =>
      flattenMetadata(
        (value as Record<string, unknown>)[key],
        prefix ? `${prefix}.${key}` : key
      )
    );
  }
  const rendered = String(value).trim();
  return rendered === '' ? [] : [{ path: prefix, value: rendered }];
}

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
/** Matches the grid's `date : 'MMM dd, yyyy'`. */
const SHORT_DATE = new Intl.DateTimeFormat('en-US', { month: 'short', day: '2-digit', year: 'numeric' });

/** Maps a sortable grid column to the backend sortBy value. */
const SORT_COLUMN_MAP: Record<string, string> = {
  date: 'invoiceDate',
  amount: 'totalAmount',
  balance: 'outstandingAmount',
};

@Component({
  selector: 'app-customer-invoice-history',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatIconModule,
    MatButtonModule,
    MatChipsModule,
    MatSlideToggleModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatTooltipModule,
  ],
  templateUrl: './customer-invoice-history.component.html',
  host: {
    class: 'component component-customer-invoice-history',
  },
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', opacity: 0 })),
      state('expanded', style({ height: '*', opacity: 1 })),
      transition('expanded <=> collapsed', animate('300ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class CustomerInvoiceHistoryComponent implements OnInit, OnChanges {
  private customersService = inject(CustomersService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private userStore = inject(UserStore);

  @Input() customerId!: string;
  @Input() customerData?: any;
  @Input() hasPaymentMethods: boolean = false;
  /** The profile keeps a customer ledger, so each document carries a ledger status. */
  @Input() ledgerEnabled = false;
  /** The reader can open the Ledger tab (drives the "View in ledger" link). */
  @Input() canViewLedger = false;
  @Output() collectClicked = new EventEmitter<void>();
  /** Open the Ledger tab narrowed to this document. */
  @Output() viewLedger = new EventEmitter<string>();

  invoices: InvoiceModel[] = [];
  isLoading = false;

  // ── Filter / sort / pagination state ──────────────────────────────────────
  readonly typeChips = TYPE_CHIPS;
  /**
   * The document types this customer holds under the current Hide Completed setting, from
   * FTE. Null until loaded (or if the lookup fails), in which case every chip is offered.
   */
  presentTypes: Set<string> | null = null;
  /** Selected chip keys; empty = "All" (no type filter). */
  selectedTypeKeys = new Set<string>();
  /**
   * Hide fully-paid (zero-balance) documents and those the ERP stopped listing
   * (NotPayable / NotUsable, which keep their balance) — on by default.
   */
  hideCompleted = true;
  /** Free-text search on the invoice number (server-side, debounced). */
  searchTerm = '';
  private searchDebounce?: ReturnType<typeof setTimeout>;

  pageIndex = 0;
  pageSize = 25;
  readonly pageSizeOptions = [10, 25, 50, 100];
  total = 0;

  sortBy: string | null = 'invoiceDate';
  /** Starts at the default for Hide Completed being on — oldest first. See defaultSortOrder. */
  sortOrder: 'asc' | 'desc' = 'asc';

  /**
   * The default invoice-date direction, decided by Hide Completed. With completed
   * documents hidden the list is what is still open, read oldest first (what to collect
   * next); with them shown it is the full history, read newest first. A click on the
   * Invoice Date header still overrides it.
   */
  defaultSortOrder(): 'asc' | 'desc' {
    return this.hideCompleted ? 'asc' : 'desc';
  }

  // ── Expansion / timeline ──────────────────────────────────────────────────
  expandedInvoiceId: string | null = null;
  timelineCache: Record<string, InvoiceTimelineEvent[]> = {};
  timelineLoading = new Set<string>();

  // ── Metadata drawer (nested inside the expanded row) ──────────────────────
  //
  // Separate from the row expansion on purpose: the metadata blob is unbounded
  // and vendor-shaped, so it is fetched only when this inner section is opened,
  // never as a side effect of expanding a row.
  metadataOpen = new Set<string>();
  metadataCache: Record<string, MetadataEntry[]> = {};
  metadataLoading = new Set<string>();
  metadataError = new Set<string>();

  private readonly baseColumns: string[] = [
    'expand',
    'invoiceNumber',
    'invoiceType',
    'date',
    'amount',
    'totalPaid',
    'balance',
    'status',
    'actions',
  ];

  /** The Ledger column only exists where the profile keeps a ledger. */
  get displayedColumns(): string[] {
    if (!this.ledgerEnabled) return this.baseColumns;
    const columns = [...this.baseColumns];
    columns.splice(columns.indexOf('status') + 1, 0, 'ledger');
    return columns;
  }

  ngOnInit(): void {
    if (this.customerId) {
      this.loadInvoices();
      this.loadPresentTypes();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customerId'] && !changes['customerId'].firstChange) {
      this.resetAndReload();
      this.loadPresentTypes();
    }
  }

  /**
   * The chips worth offering: Invoice and Credit Memo always; any other type only when the
   * customer holds one (counted under Hide Completed, so a chip never filters the list to
   * nothing); and any chip already selected, so it can still be switched off.
   */
  get visibleTypeChips(): TypeChip[] {
    const present = this.presentTypes;
    if (!present) return this.typeChips;
    return this.typeChips.filter(
      (chip) =>
        ALWAYS_SHOWN_CHIPS.has(chip.key) ||
        this.selectedTypeKeys.has(chip.key) ||
        chip.types.some((type) => present.has(type))
    );
  }

  /** Ask FTE which document types this customer holds (all of them, not one page's). */
  private loadPresentTypes(): void {
    if (!this.customerId) return;
    this.customersService.getCustomerInvoiceTypes(this.customerId, {
      hasBalance: this.hideCompleted,
      excludeErpUnlisted: this.hideCompleted,
    }).subscribe({
      next: (types) => {
        this.presentTypes = new Set(
          types.filter((t) => t.count > 0 && t.invoiceType).map((t) => t.invoiceType as string)
        );
      },
      error: () => {
        this.presentTypes = null;
      },
    });
  }

  // ── Data loading ──────────────────────────────────────────────────────────

  private selectedInvoiceTypes(): string[] {
    const types: string[] = [];
    for (const chip of this.typeChips) {
      if (this.selectedTypeKeys.has(chip.key)) {
        types.push(...chip.types);
      }
    }
    return types;
  }

  loadInvoices(): void {
    if (!this.customerId) {
      return;
    }

    this.isLoading = true;
    this.customersService
      .getCustomerInvoicesPaged(this.customerId, {
        invoiceTypes: this.selectedInvoiceTypes(),
        hasBalance: this.hideCompleted,
        excludeErpUnlisted: this.hideCompleted,
        search: this.searchTerm,
        page: this.pageIndex + 1,
        limit: this.pageSize,
        sortBy: this.sortBy ?? undefined,
        sortOrder: this.sortOrder,
      })
      .subscribe({
        next: (result) => {
          this.invoices = result.invoices;
          this.total = result.total;
          this.isLoading = false;
        },
        error: () => {
          this.invoices = [];
          this.total = 0;
          this.isLoading = false;
        },
      });
  }

  /** Reload from page 1, collapsing any open row (used after filter changes). */
  private resetAndReload(): void {
    this.pageIndex = 0;
    this.expandedInvoiceId = null;
    this.loadInvoices();
  }

  // ── Filter handlers ───────────────────────────────────────────────────────

  isTypeSelected(key: string): boolean {
    return this.selectedTypeKeys.has(key);
  }

  get isAllTypes(): boolean {
    return this.selectedTypeKeys.size === 0;
  }

  toggleType(key: string): void {
    if (this.selectedTypeKeys.has(key)) {
      this.selectedTypeKeys.delete(key);
    } else {
      this.selectedTypeKeys.add(key);
    }
    this.resetAndReload();
  }

  selectAllTypes(): void {
    if (this.selectedTypeKeys.size === 0) {
      return;
    }
    this.selectedTypeKeys.clear();
    this.resetAndReload();
  }

  /** Switching Hide Completed re-applies that mode's default sort (invoice date, see defaultSortOrder). */
  onHideCompletedChange(): void {
    this.sortBy = 'invoiceDate';
    this.sortOrder = this.defaultSortOrder();
    this.resetAndReload();
    this.loadPresentTypes();
  }

  /** Debounced free-text search on the invoice number. */
  onSearchChange(): void {
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }
    this.searchDebounce = setTimeout(() => this.resetAndReload(), 350);
  }

  // ── Pagination / sorting ──────────────────────────────────────────────────

  onPage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.expandedInvoiceId = null;
    this.loadInvoices();
  }

  /**
   * The grid column the current sort is on, bound into `matSortActive` so the
   * header arrow always reflects what the server is sorting by — including the
   * default (Invoice Date, newest first) before anyone has clicked.
   *
   * Bound rather than left to MatSort's own memory because MatSort's state does
   * not survive the table being re-created, and a header that forgets it was
   * sorted makes every click a "first" click: Invoice Date then sorts ascending
   * on every click and never reverses.
   */
  get sortActive(): string {
    return (
      Object.keys(SORT_COLUMN_MAP).find((col) => SORT_COLUMN_MAP[col] === this.sortBy) ?? 'date'
    );
  }

  get sortDirection(): SortDirection {
    return this.sortOrder;
  }

  onSortChange(sort: Sort): void {
    if (!sort.direction || !SORT_COLUMN_MAP[sort.active]) {
      this.sortBy = 'invoiceDate';
      this.sortOrder = this.defaultSortOrder();
    } else {
      this.sortBy = SORT_COLUMN_MAP[sort.active];
      this.sortOrder = sort.direction as 'asc' | 'desc';
    }
    this.pageIndex = 0;
    this.loadInvoices();
  }

  // ── Expansion / timeline ──────────────────────────────────────────────────

  hasEvents(invoice: InvoiceModel): boolean {
    return (invoice.eventCount ?? 0) > 0;
  }

  isExpanded(invoiceRef: string): boolean {
    return this.expandedInvoiceId === invoiceRef;
  }

  toggleExpand(invoice: InvoiceModel): void {
    const ref = invoice.invoiceId;
    if (this.expandedInvoiceId === ref) {
      this.expandedInvoiceId = null;
      return;
    }
    this.expandedInvoiceId = ref;
    if (!this.timelineCache[ref] && !this.timelineLoading.has(ref)) {
      this.timelineLoading.add(ref);
      this.customersService.getInvoiceTimeline(ref).subscribe({
        next: (events) => {
          this.timelineCache[ref] = events;
          this.timelineLoading.delete(ref);
        },
        error: () => {
          this.timelineCache[ref] = [];
          this.timelineLoading.delete(ref);
        },
      });
    }
  }

  isTimelineLoading(invoiceRef: string): boolean {
    return this.timelineLoading.has(invoiceRef);
  }

  timelineFor(invoiceRef: string): InvoiceTimelineEvent[] {
    return this.timelineCache[invoiceRef] ?? [];
  }

  /** Human label for a CustomerInvoiceEvent type. */
  eventLabel(eventType: string): string {
    return eventType.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  eventIcon(eventType: string): string {
    // Ahead of the generic matches: `ImportMarkedNotPayable` would otherwise fall to
    // the `Import` family's icon and read as a document being created.
    if (/NotPayableResolved/i.test(eventType)) return 'task_alt';
    if (/NotPayableReopened/i.test(eventType)) return 'restart_alt';
    if (/NotPayable/i.test(eventType)) return 'money_off';
    if (/Payment/i.test(eventType) && !/Declined/i.test(eventType)) return 'payments';
    if (/Declined/i.test(eventType)) return 'block';
    if (/Void|Reversed/i.test(eventType)) return 'undo';
    if (/Created|Imported/i.test(eventType)) return 'note_add';
    if (/Adjust|Reclassify|TypeChanged|TotalRevised/i.test(eventType)) return 'tune';
    if (/Credit/i.test(eventType)) return 'card_giftcard';
    return 'history';
  }

  // ── Status display ────────────────────────────────────────────────────────

  getStatusLabel(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'paid':
      case 'complete':
        return 'Paid';
      case 'pendingpaid':
        return 'Pending Paid';
      case 'partiallypaid':
      case 'partial':
        return 'Partially Paid';
      case 'overpaid':
        return 'Overpaid';
      case 'scheduled':
        return 'Scheduled';
      case 'declined':
        return 'Declined';
      case 'autopay':
        return 'Autopay';
      case 'overdue':
        return 'Overdue';
      case 'cancelled':
        return 'Cancelled';
      case 'notpayable':
        return 'Not Payable';
      case 'notusable':
        return 'Not Usable';
      // Credit-type documents (FTE models/creditDocumentStatus.ts).
      case 'available':
        return 'Available';
      case 'partiallyapplied':
        return 'Partially Applied';
      case 'fullyapplied':
        return 'Fully Applied';
      case 'new':
        return 'New';
      default:
        return status;
    }
  }

  /**
   * The ERP stopped listing this invoice as open and nothing on file explains it as paid.
   * FTE refuses any payment against it (409 `INVOICE_NOT_PAYABLE`), so it is never offered
   * for collection. It keeps its balance, but "Hide Completed" hides it anyway; it shows
   * only with that toggle off.
   */
  isNotPayable(item: InvoiceModel): boolean {
    return item.status === 'notpayable';
  }

  /**
   * The credit-memo mirror: the ERP stopped listing this credit as open and nothing showed
   * it applied, so FTE refuses to apply it (409 `CREDIT_APPLICATION_INVALID`, problem
   * `credit_not_usable`) while the credit keeps its (negative) balance.
   */
  isNotUsable(item: InvoiceModel): boolean {
    return item.status === 'notusable';
  }

  /**
   * Either side of the same fact — the ERP stopped listing the document. One predicate, so
   * the chip, the expanded notice and the withheld Collect button can never disagree.
   */
  isErpUnlisted(item: InvoiceModel): boolean {
    return this.isNotPayable(item) || this.isNotUsable(item);
  }

  /** "Not payable" / "Not usable" — the lead-in on the expanded row's notice. */
  erpUnlistedTitle(item: InvoiceModel): string {
    return this.isNotUsable(item) ? 'Not usable.' : 'Not payable.';
  }

  /**
   * Why the document cannot be used — the badge tooltip and the expanded row's notice. An
   * invoice is blocked from payment; a credit is blocked from being applied. Both are the
   * same marker, so both are worded from it.
   */
  /** The document type as shown to a user — `ERPInvoice` reads "Invoice". */
  invoiceTypeLabel(type?: string | null): string {
    if (!type) return '—';
    return INVOICE_TYPE_LABELS[type] ?? type;
  }

  /** A credit-type document (credit memo, credit on file, coupon, adjustment). */
  isCreditDocument(item: InvoiceModel): boolean {
    return CREDIT_DOCUMENT_TYPES.has(item.invoiceType ?? '');
  }

  /** Total paid — or, on a credit, total APPLIED, which is shown positive. */
  paidOrApplied(item: InvoiceModel): number {
    const paid = Number(item.amountPaid ?? 0);
    return this.isCreditDocument(item) ? Math.abs(paid) : paid;
  }

  /** Payment lines held against the document — on a credit, the applications, shown positive. */
  accountedForDisplay(item: InvoiceModel): number | undefined {
    if (item.accountedForPaymentsTotal === undefined) return undefined;
    const v = Number(item.accountedForPaymentsTotal);
    return this.isCreditDocument(item) ? Math.abs(v) : v;
  }

  notPayableHint(item: InvoiceModel): string {
    const marker = item.notPayable;
    if (marker?.reason === 'awaiting_erp_balance') {
      return awaitingErpBalanceHint(this.isNotUsable(item), marker.fileName);
    }
    const when = marker?.asOf && !isNaN(marker.asOf.getTime()) ? ` on ${SHORT_DATE.format(marker.asOf)}` : '';
    const file = marker?.fileName ? ` (${marker.fileName})` : '';
    if (this.isNotUsable(item)) {
      return (
        `The ERP stopped listing this credit as open${when}${file} and nothing on file shows it was applied. ` +
        `It cannot be applied to an invoice; the credit shown is the ERP's last figure.`
      );
    }
    return (
      `The ERP stopped listing this invoice as open${when}${file} and nothing on file explains it as paid. ` +
      `Payments are blocked; the balance shown is the ERP's last figure.`
    );
  }

  // ── Metadata drawer ───────────────────────────────────────────────────────

  isMetadataOpen(invoiceRef: string): boolean {
    return this.metadataOpen.has(invoiceRef);
  }

  isMetadataLoading(invoiceRef: string): boolean {
    return this.metadataLoading.has(invoiceRef);
  }

  hasMetadataError(invoiceRef: string): boolean {
    return this.metadataError.has(invoiceRef);
  }

  metadataFor(invoiceRef: string): MetadataEntry[] {
    return this.metadataCache[invoiceRef] ?? [];
  }

  /**
   * Open/close the drawer, fetching on the first open only. A failed fetch clears
   * its cache entry so reopening retries, rather than leaving the drawer
   * permanently empty with no way to try again.
   */
  toggleMetadata(invoice: InvoiceModel): void {
    const ref = invoice.invoiceId;
    if (this.metadataOpen.has(ref)) {
      this.metadataOpen.delete(ref);
      return;
    }
    this.metadataOpen.add(ref);

    if (this.metadataCache[ref] || this.metadataLoading.has(ref)) {
      return;
    }
    this.metadataLoading.add(ref);
    this.metadataError.delete(ref);
    this.customersService.getInvoiceMetadata(ref).subscribe({
      next: (response: InvoiceMetadataResponse) => {
        this.metadataCache[ref] = flattenMetadata(response.invoiceMetadata ?? {}).filter(
          (entry) => !HIDDEN_METADATA_PATHS.has(entry.path)
        );
        this.metadataLoading.delete(ref);
      },
      error: () => {
        this.metadataLoading.delete(ref);
        this.metadataError.add(ref);
      },
    });
  }

  /** Chip class for the accounting verdict — only a mismatch should draw the eye. */
  ledgerStatusLabel(status?: LedgerDocumentStatus | null): string {
    return status ? LEDGER_DOCUMENT_STATUS_LABELS[status] ?? status : '';
  }

  ledgerStatusHint(item: InvoiceModel): string {
    if (!item.ledgerStatus) return 'Not yet compared with the customer ledger';
    const hint = LEDGER_DOCUMENT_STATUS_HINTS[item.ledgerStatus] ?? '';
    return item.ledgerVariance !== null && item.ledgerVariance !== undefined && !isZeroAmount(item.ledgerVariance)
      ? `${hint}. Ledger ${USD.format(item.ledgerBalance ?? 0)} vs ERP ${USD.format(item.balance ?? 0)}.`
      : hint;
  }

  ledgerStatusClass(status?: LedgerDocumentStatus | null): string {
    switch (status) {
      case 'Balanced':
        return 'ledger-chip ledger-balanced';
      case 'LedgerOver':
        return 'ledger-chip ledger-over';
      case 'LedgerUnder':
        return 'ledger-chip ledger-under';
      case 'NoLedger':
        return 'ledger-chip ledger-none';
      default:
        return 'ledger-chip';
    }
  }

  openLedger(item: InvoiceModel): void {
    this.viewLedger.emit(item.invoiceId);
  }

  accountingStatusClass(status?: string): string {
    switch (status) {
      case 'Overaccounted':
        return 'accounting-chip accounting-overaccounted';
      case 'Unaccounted':
        return 'accounting-chip accounting-unaccounted';
      case 'Reconciled':
        return 'accounting-chip accounting-reconciled';
      default:
        return 'accounting-chip';
    }
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  collectPayment(invoiceId: string): void {
    this.collectClicked.emit();
    this.router.navigate(['/payment/collect'], {
      queryParams: {
        invoiceId: invoiceId,
        customerId: this.customerId,
        skipToStep: '3',
      },
      // Collect uses this rather than fetching the customer again. It checks the id, and fetches
      // when the state is missing (a refresh or a pasted link).
      state: { customer: this.customerData },
    });
  }

  isTestInvoicesEnabled(): boolean {
    return this.userStore?.activeProfile()?.profileDetail?.profileAdminSettings?.enableTestInvoices || false;
  }

  openTestAddInvoicesModal(): void {
    const dialogRef = this.dialog.open(TestAddInvoicesModalComponent, {
      width: '900px',
      maxWidth: '95vw',
      disableClose: true,
      panelClass: 'test-add-invoices-dialog',
      data: {
        customerId: this.customerId,
        enableAutopay: this.customerData?.customerSettings?.enableAutopay || false,
      },
    });

    dialogRef.afterClosed().subscribe((invoices: TestInvoice[] | undefined) => {
      if (invoices && invoices.length > 0) {
        this.addTestInvoices(invoices);
      }
    });
  }

  addTestInvoices(testInvoices: TestInvoice[]): void {
    this.customersService.addTestInvoices(this.customerId, testInvoices).subscribe({
      next: () => {
        this.snackBar.open(`Successfully added ${testInvoices.length} test invoices!`, 'Close', {
          duration: 5000,
        });
        this.resetAndReload();
      },
      error: (error) => {
        this.snackBar.open(`Failed to add test invoices: ${error.message}`, 'Close', {
          duration: 5000,
        });
      },
    });
  }
}
